import React from "react";
import styled from "styled-components";
import { IPageSectionResponse } from "models/response/page-section-response";
import { SectionImage, SectionTitle } from "../custom-page.styles";

const VideoContainer = styled.div`
  margin-bottom: 30px;
`;

const VideoWrapper = styled.div`
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const VideoUnavailable = styled.div`
  padding: 20px;
  border-radius: 8px;
  background: #f5f5f5;
  color: #666;
  font-size: 0.95rem;

  a {
    color: #1976d2;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 25px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const ItemCard = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  ${props => props.onClick && `
    cursor: pointer;
  `}
`;

const ItemImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 15px;
`;

const ItemTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ItemSubtitle = styled.h4`
  font-size: 16px;
  font-weight: 500;
  color: #666;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ItemDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

interface VideoEmbedSectionProps {
    section: IPageSectionResponse;
}

// Função para extrair e formatar URL do vídeo
const getEmbedUrl = (url: string): string | null => {
    if (!url) return null;

    // YouTube: watch?v=, embed/, v/, shorts/, live/ e youtu.be/
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts|live)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch && youtubeMatch[1]) {
        return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoRegex = /vimeo\.com\/(?:.*\/)?(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch && vimeoMatch[1]) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    // Se já é uma URL de embed, retorna ela mesma
    if (url.includes('youtube.com/embed') || url.includes('player.vimeo.com')) {
        return url;
    }

    return null;
};

export const VideoEmbedSection: React.FC<VideoEmbedSectionProps> = ({ section }) => {
    const embedUrl = getEmbedUrl(section.url || '');
    const hasItems = !!section.items && section.items.length > 0;

    if (!embedUrl && !section.title && !section.imageUrl && !hasItems) {
        return null;
    }

    const sortedItems = hasItems ? [...section.items!].sort((a, b) => a.order - b.order) : [];

    const handleItemClick = (url?: string) => {
        if (url) {
            window.open(url, '_blank');
        }
    };

    return (
        <VideoContainer>
            {section.title && <SectionTitle>{section.title}</SectionTitle>}
            {section.imageUrl && <SectionImage src={section.imageUrl} alt={section.title || ''} />}
            {embedUrl ? (
                <VideoWrapper>
                    <iframe
                        src={embedUrl}
                        title={section.title || 'Vídeo'}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </VideoWrapper>
            ) : (
                section.url && (
                    <VideoUnavailable>
                        Não foi possível carregar o vídeo.{' '}
                        <a href={section.url} target="_blank" rel="noopener noreferrer">
                            Assistir no link original
                        </a>
                    </VideoUnavailable>
                )
            )}
            {hasItems && (
                <ItemsGrid>
                    {sortedItems.map((item) => (
                        <ItemCard
                            key={item.id}
                            onClick={item.url ? () => handleItemClick(item.url) : undefined}
                        >
                            {item.imageUrl && (
                                <ItemImage src={item.imageUrl} alt={item.title || ''} />
                            )}
                            {item.title && <ItemTitle>{item.title}</ItemTitle>}
                            {item.subtitle && <ItemSubtitle>{item.subtitle}</ItemSubtitle>}
                            {item.description && <ItemDescription>{item.description}</ItemDescription>}
                        </ItemCard>
                    ))}
                </ItemsGrid>
            )}
        </VideoContainer>
    );
};
